/**
 * Aviso de cookies (LGPD) e registro de visualizações das páginas do site.
 * A lógica do Google Analytics fica em ../analytics.ts.
 *
 * @author Equipe VendeAi
 * @since 1.4.0
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import {
  Consent,
  OPEN_EVENT,
  disableAnalytics,
  loadAnalytics,
  readConsent,
  saveConsent,
  trackPageView,
} from '../analytics';

/**
 * Aviso de cookies + registro de page_view nas páginas do site.
 * Não renderiza nada no build (pré-renderização) nem sem ID configurado.
 */
export function SiteAnalytics({
  privacyUrl,
  measurementId,
}: {
  privacyUrl?: string | null;
  /** ID do GA4 já resolvido (painel ou build); vazio desliga o analytics */
  measurementId: string;
}) {
  const enabled = measurementId !== '';
  const location = useLocation();
  const [consent, setConsent] = useState<Consent | null>(null);
  const [open, setOpen] = useState(false);

  // Lê a escolha só no navegador (o HTML pré-renderizado não traz o aviso)
  useEffect(() => {
    if (!enabled) return;
    const saved = readConsent();
    setConsent(saved);
    setOpen(saved === null);
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (consent === 'granted') loadAnalytics(measurementId);
    else if (consent === 'denied') disableAnalytics(measurementId);
  }, [consent, enabled, measurementId]);

  // Uma visualização por página (inclusive navegação sem recarregar)
  useEffect(() => {
    if (!enabled || consent !== 'granted') return;
    trackPageView(location.pathname);
  }, [consent, enabled, location.pathname]);

  if (!enabled || !open) return null;

  const decide = (value: Consent) => {
    saveConsent(value);
    setConsent(value);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Preferências de cookies"
      className="fixed inset-x-3 bottom-24 z-[45] mx-auto max-w-xl rounded-2xl border border-white/10 bg-[#0d1120]/95 p-5 text-sm text-slate-300 shadow-2xl backdrop-blur-xl sm:inset-x-6 lg:bottom-6"
    >
      <div className="flex gap-3">
        <Cookie size={20} className="mt-0.5 shrink-0 text-violet-300" aria-hidden />
        <p>
          Usamos cookies de análise (Google Analytics) para entender como o site é usado e
          melhorá-lo. Nada é coletado se você recusar.
          {privacyUrl && (
            <>
              {' '}
              <a
                href={privacyUrl}
                {...(privacyUrl.startsWith('/')
                  ? {}
                  : { target: '_blank', rel: 'noopener noreferrer' })}
                className="font-semibold text-violet-300 underline-offset-2 hover:underline"
              >
                Política de privacidade
              </a>
            </>
          )}
        </p>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => decide('denied')}
          className="rounded-xl border border-white/15 px-4 py-2 font-semibold text-slate-200 transition hover:bg-white/5"
        >
          Recusar
        </button>
        <button
          type="button"
          onClick={() => decide('granted')}
          className="rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2 font-semibold text-white transition hover:opacity-90"
        >
          Aceitar
        </button>
      </div>
    </div>
  );
}
